'use client';

/**
 * CartCheckout — botón + modal que procesa el checkout de un grupo de
 * items del mismo vendedor.
 *
 * Feature (v136): itera creando 1 orden por item (preservando INV-MKT-12).
 * Cada orden incluye `cartTotalCredits` = suma total del carrito de ese
 * vendedor, para que el BE valide el mínimo de envío contra el total.
 *
 * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): rewrite to show the SAME
 * information as the "Comprar ahora" modal on the product detail page:
 *   - RadioGroup for shipping method (ship / pickup) when both are offered.
 *   - Shipping cost display (base cost + free-shipping threshold).
 *   - Free-shipping badge when threshold is met.
 *   - Total with shipping included (subtotal + shippingCost).
 *   - Pickup block (address, hours, instructions, Google Maps link, confirm checkbox).
 *   - Idempotency-Key header per order (anti-P-043 — no duplicate orders on retry).
 *   - Error mapping via `mapCreateError` (better UX — known BE codes → Spanish messages).
 *   - Pickup-only sellers: auto-select pickup (no ship option).
 *   - Ship-only sellers: auto-select ship (no pickup option).
 *   - Items without fulfillment data (old cart items): graceful degradation
 *     callout asking the user to re-add the item from the product page.
 *
 * Founder directive 2026-11-05: "Cuando le doy comprar ahora en el carrito
 * no muestra la misma UI que cuando se le da comprar ahora directamente al
 * producto mejoralo, para que muestre las opciones de envío, valor de envío
 * y se sume al total, que tampoco lo está haciendo, de envío gratis si
 * califica."
 *
 * Anti-P-041: 0 `as any` in new code.
 * Anti-P-042: 0 `console.*`.
 * Anti-P-013: errors surfaced via inline alert + toast.
 * Anti-P-043: idempotency key per order prevents duplicates on retry.
 * Anti-P-347: VERBATIM mirror of `checkout-modal/helpers.ts:mapCreateError`
 *             + `physical-details-step.tsx` RadioGroup + summary breakdown.
 * Anti-P-382: REUSES `computeCartShipping` from `cart-pricing.ts` (single
 *             source of truth for cart-level shipping calc). REUSES
 *             `mapCreateError` from `checkout-modal/helpers.ts`.
 * Anti-P-383: this file ≤400L target (split logic into `cart-pricing.ts`).
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { Route } from 'next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShoppingBag,
  Truck,
  MapPin,
  ExternalLink,
  Info,
} from 'lucide-react';
import type { CartItem } from './store';
import { computeCartShipping, type CartShippingQuote } from './cart-pricing';
import { mapCreateError } from '@/components/market/checkout-modal/helpers';
import { api, ApiClientError } from '@/lib/api-client';
import { toast } from 'sonner';

interface CartCheckoutProps {
  sellerItems: CartItem[];
  cartTotalCredits: string;
  sellerName: string;
  onComplete: () => void;
}

type CheckoutState = 'idle' | 'processing' | 'success' | 'partial' | 'error';
type FulfillmentMethod = 'ship' | 'pickup';

export function CartCheckout({
  sellerItems,
  cartTotalCredits,
  sellerName,
  onComplete,
}: CartCheckoutProps) {
  const router = useRouter();
  const [state, setState] = useState<CheckoutState>('idle');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<
    Array<{ productId: string; title: string; success: boolean; error?: string }>
  >([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Shipping address form state.
  const [shippingName, setShippingName] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingCountry, setShippingCountry] = useState('Colombia');
  const [shippingZip, setShippingZip] = useState('');
  const [shippingIndications, setShippingIndications] = useState('');

  // Pickup confirmation.
  const [pickupConfirmed, setPickupConfirmed] = useState(false);

  // Fulfillment method selection.
  const [fulfillmentMethod, setFulfillmentMethod] = useState<FulfillmentMethod>('ship');

  // Compute the shipping quote (memoized — recomputes when items change).
  const quote: CartShippingQuote | null = useMemo(
    () => computeCartShipping(sellerItems),
    [sellerItems],
  );

  // Auto-select the only available fulfillment method (pickup-only or ship-only sellers).
  useEffect(() => {
    if (!quote) return;
    if (quote.ships && !quote.pickup) {
      setFulfillmentMethod('ship');
    } else if (!quote.ships && quote.pickup) {
      setFulfillmentMethod('pickup');
    }
  }, [quote]);

  // Reset state when the modal opens.
  useEffect(() => {
    if (open) {
      setState('idle');
      setResults([]);
      setSubmitError(null);
      setPickupConfirmed(false);
    }
  }, [open]);

  const isAddressValid =
    shippingName.trim() &&
    shippingPhone.trim() &&
    shippingAddress.trim() &&
    shippingCity.trim() &&
    shippingCountry.trim() &&
    shippingZip.trim();

  // Effective validation — depends on the selected fulfillment method.
  const isFormValid =
    fulfillmentMethod === 'pickup'
      ? pickupConfirmed
      : isAddressValid;

  // The total displayed to the user (subtotal + shipping if ship method).
  const displayTotal = quote
    ? fulfillmentMethod === 'ship'
      ? quote.grandTotal.toFixed(2)
      : quote.subtotal.toFixed(2)
    : cartTotalCredits;

  const handleCheckout = useCallback(async () => {
    if (!isFormValid) {
      setSubmitError(
        fulfillmentMethod === 'pickup'
          ? 'Debes confirmar que pasarás a recoger el pedido.'
          : 'Por favor completa todos los campos de la dirección de envío.',
      );
      return;
    }

    setState('processing');
    setResults([]);
    setSubmitError(null);

    const orderResults: typeof results = [];

    // TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): generate ONE idempotency
    // key per order (anti-P-043 — if the network fails mid-loop, retries
    // don't create duplicate orders; the BE deduplicates by key).
    const baseIdempotency = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    for (let idx = 0; idx < sellerItems.length; idx++) {
      const item = sellerItems[idx];
      const idempotencyKey = `${baseIdempotency}-${idx}`;
      try {
        // TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): the BE's order
        // create endpoint dispatches by `productType`. For now, the cart
        // only supports physical items (the "Añadir al carrito" button is
        // only shown for `product.type === 'physical'` in product-buy-button).
        // If a digital/service item slips through (e.g., from an old cart),
        // the BE will reject with PRODUCT_NOT_PHYSICAL — mapped to a clear
        // message via mapCreateError. Future: dispatch to the correct
        // endpoint based on item.type (digital/service have their own
        // create-order endpoints).
        await api.market.orders.create(
          {
            productType: 'physical',
            productId: item.productId,
            quantity: item.quantity,
            fulfillmentMethod,
            cartTotalCredits,
            ...(fulfillmentMethod === 'ship'
              ? {
                  shippingAddress: {
                    name: shippingName,
                    phone: shippingPhone,
                    address: shippingAddress,
                    city: shippingCity,
                    country: shippingCountry,
                    zip: shippingZip,
                    indicaciones: shippingIndications.trim() || undefined,
                  },
                }
              : {}),
            variationSelections: item.variationSelections,
          },
          { idempotencyKey },
        );
        orderResults.push({
          productId: item.productId,
          title: item.productTitle,
          success: true,
        });
      } catch (err) {
        let msg: string;
        if (err instanceof ApiClientError) {
          // SPEC-157 — single-purchase recurring: the buyer already owns
          // this product. Intercept with a friendly toast + skip (NOT an
          // error state — the buyer is an existing customer).
          if (err.code === 'SINGLE_PURCHASE_ALREADY_BOUGHT') {
            toast.info('Ya tienes este producto', {
              description: `${item.productTitle} — ya lo compraste antes.`,
              duration: 6000,
            });
            orderResults.push({
              productId: item.productId,
              title: item.productTitle,
              success: true, // not an error — the order was "already done"
            });
            continue;
          }
          const mapped = mapCreateError(err);
          msg = mapped ?? err.message;
        } else {
          msg = err instanceof Error ? err.message : 'Error desconocido';
        }
        orderResults.push({
          productId: item.productId,
          title: item.productTitle,
          success: false,
          error: msg,
        });
      }
    }

    setResults(orderResults);
    const successCount = orderResults.filter((r) => r.success).length;
    if (successCount === orderResults.length) {
      setState('success');
      toast.success('¡Compra exitosa!', {
        description: `${successCount} producto(s) de ${sellerName} comprados correctamente.`,
        duration: 6000,
      });
    } else if (successCount > 0) {
      setState('partial');
      toast.warning('Compra parcial', {
        description: `${successCount} de ${orderResults.length} productos comprados. Revisa los errores.`,
        duration: 8000,
      });
    } else {
      setState('error');
      toast.error('No se pudo completar la compra', {
        description: 'Todos los productos fallaron. Revisa los errores.',
        duration: 8000,
      });
    }
  }, [
    isFormValid,
    fulfillmentMethod,
    sellerItems,
    cartTotalCredits,
    shippingName,
    shippingPhone,
    shippingAddress,
    shippingCity,
    shippingCountry,
    shippingZip,
    shippingIndications,
    sellerName,
    results,
  ]);

  const handleClose = () => {
    setOpen(false);
    if (state === 'success') {
      onComplete();
      router.push('/app/market/compras' as Route);
    }
    setState('idle');
    setResults([]);
  };

  // Block closing the modal mid-submit (anti-P-043 — accidental close
  // would lose the in-flight orders).
  const handleOpenChange = (nextOpen: boolean) => {
    if (state === 'processing' && !nextOpen) return;
    setOpen(nextOpen);
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <ShoppingBag className="size-4 mr-1" aria-hidden="true" />
        Comprar
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {state === 'processing'
                ? 'Procesando compra...'
                : state === 'success'
                  ? '¡Compra exitosa!'
                  : state === 'partial'
                    ? 'Compra parcial'
                    : state === 'error'
                      ? 'Error en la compra'
                      : `Comprar ${sellerItems.length} producto(s) de ${sellerName}`}
            </DialogTitle>
            {state === 'idle' && (
              <DialogDescription>
                Se crearán {sellerItems.length} orden(es) individuales (1 por
                producto). El envío se calcula por vendedor.
              </DialogDescription>
            )}
          </DialogHeader>

          {state === 'processing' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="size-8 animate-spin text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                Creando órdenes para {sellerItems.length} producto(s)...
              </p>
            </div>
          )}

          {(state === 'success' || state === 'partial' || state === 'error') && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {results.map((r) => (
                <div key={r.productId} className="flex items-start gap-2 p-2 rounded-md text-sm">
                  {r.success ? (
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                  ) : (
                    <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="line-clamp-1">{r.title}</p>
                    {!r.success && r.error && (
                      <p className="text-xs text-destructive mt-0.5">{r.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {state === 'idle' && (
            <div className="space-y-4">
              {/* Graceful degradation: items without fulfillment data (old cart). */}
              {!quote && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
                  <Info className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="font-medium">Información de envío no disponible</p>
                    <p className="text-xs mt-1">
                      Estos items fueron añadidos antes de una actualización. Para ver las
                      opciones de envío, elimínalos del carrito y vuelve a añadirlos desde la
                      página del producto.
                    </p>
                  </div>
                </div>
              )}

              {/* Fulfillment method selector (only when both ship + pickup are offered). */}
              {quote && quote.ships && quote.pickup && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Método de entrega</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFulfillmentMethod('ship')}
                      aria-pressed={fulfillmentMethod === 'ship'}
                      className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${
                        fulfillmentMethod === 'ship'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40 hover:bg-muted/40'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Truck className="size-4 text-primary" aria-hidden="true" />
                        <span className="text-sm font-medium">Envío a domicilio</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {quote.freeShippingApplied
                          ? 'Gratis (umbral alcanzado)'
                          : `${quote.shippingCost} créditos`}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFulfillmentMethod('pickup')}
                      aria-pressed={fulfillmentMethod === 'pickup'}
                      className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${
                        fulfillmentMethod === 'pickup'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40 hover:bg-muted/40'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="size-4 text-primary" aria-hidden="true" />
                        <span className="text-sm font-medium">Recogida en local</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Sin costo de envío</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Shipping address form (only when ship method). */}
              {quote && fulfillmentMethod === 'ship' && (
                <div className="space-y-3 rounded-lg border p-3 bg-muted/30">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Truck className="size-4" aria-hidden="true" />
                    Dirección de envío
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="ship-name" className="text-xs">Nombre</Label>
                      <Input id="ship-name" value={shippingName} onChange={(e) => setShippingName(e.target.value)} placeholder="Juan Pérez" className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label htmlFor="ship-phone" className="text-xs">Teléfono</Label>
                      <Input id="ship-phone" value={shippingPhone} onChange={(e) => setShippingPhone(e.target.value)} placeholder="+57 300 123 4567" className="h-8 text-sm" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ship-address" className="text-xs">Dirección</Label>
                    <Input id="ship-address" value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} placeholder="Calle 123 #45-67, Apt 501" className="h-8 text-sm" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label htmlFor="ship-city" className="text-xs">Ciudad</Label>
                      <Input id="ship-city" value={shippingCity} onChange={(e) => setShippingCity(e.target.value)} placeholder="Bogotá" className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label htmlFor="ship-country" className="text-xs">País</Label>
                      <Input id="ship-country" value={shippingCountry} onChange={(e) => setShippingCountry(e.target.value)} placeholder="Colombia" className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label htmlFor="ship-zip" className="text-xs">Código postal</Label>
                      <Input id="ship-zip" value={shippingZip} onChange={(e) => setShippingZip(e.target.value)} placeholder="110111" className="h-8 text-sm" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ship-indications" className="text-xs">
                      Indicaciones para la entrega <span className="text-muted-foreground">(opcional)</span>
                    </Label>
                    <Textarea
                      id="ship-indications"
                      value={shippingIndications}
                      onChange={(e) => setShippingIndications(e.target.value)}
                      placeholder="Ej: tocar timbre 2do piso, dejar con el portero..."
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                  {/* Free-shipping badge + threshold hint. */}
                  {quote.freeShippingThreshold !== null && (
                    <div className="text-xs">
                      {quote.freeShippingApplied ? (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          <CheckCircle2 className="size-3" aria-hidden="true" />
                          ¡Envío gratis! (umbral de {quote.freeShippingThreshold} créditos alcanzado)
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Envío gratis desde {quote.freeShippingThreshold} créditos. Te faltan{' '}
                          {(quote.freeShippingThreshold - quote.subtotal).toFixed(2)} cr.
                        </span>
                      )}
                    </div>
                  )}
                  {quote.minOrderCredits > 0 && quote.subtotal < quote.minOrderCredits && (
                    <div className="text-xs text-amber-700 dark:text-amber-300">
                      ⚠️ Mínimo de orden: {quote.minOrderCredits} créditos. Te faltan{' '}
                      {(quote.minOrderCredits - quote.subtotal).toFixed(2)} cr.
                    </div>
                  )}
                </div>
              )}

              {/* Pickup block (only when pickup method). */}
              {quote && fulfillmentMethod === 'pickup' && quote.pickupAddress && (
                <div className="space-y-3 rounded-lg border p-3 bg-muted/30">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <MapPin className="size-4" aria-hidden="true" />
                    Recogida en local
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{quote.pickupAddress}</p>
                    {quote.pickupHours && (
                      <p className="text-xs text-muted-foreground">Horario: {quote.pickupHours}</p>
                    )}
                    {quote.pickupInstructions && (
                      <p className="text-xs text-muted-foreground">{quote.pickupInstructions}</p>
                    )}
                  </div>
                  {quote.pickupAddress && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(quote.pickupAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="size-3" aria-hidden="true" />
                      Ver en Google Maps
                    </a>
                  )}
                  <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pickupConfirmed}
                        onChange={(e) => setPickupConfirmed(e.target.checked)}
                        className="mt-0.5"
                        aria-label="Confirmo que pasaré a recoger el pedido"
                      />
                      <span className="text-xs">
                        Confirmo que pasaré a recoger el pedido en la dirección y horario indicados.
                      </span>
                  </label>
                </div>
              )}

              {/* Items + cost breakdown summary. */}
              <div className="space-y-2 rounded-lg border p-3">
                <div className="text-sm font-medium">Resumen</div>
                <div className="space-y-1">
                  {sellerItems.map((item) => (
                    <div key={item.productId} className="flex items-center justify-between text-sm">
                      <span className="line-clamp-1">{item.productTitle}</span>
                      <span className="text-muted-foreground shrink-0 ml-2">
                        {item.quantity} × {item.priceCredits} cr
                      </span>
                    </div>
                  ))}
                </div>
                {quote && (
                  <div className="border-t pt-2 mt-2 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{quote.subtotal.toFixed(2)} cr</span>
                    </div>
                    {fulfillmentMethod === 'ship' && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Envío</span>
                        {quote.freeShippingApplied ? (
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium">Gratis</span>
                        ) : (
                          <span>{quote.shippingCost.toFixed(2)} cr</span>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                      <span>Total</span>
                      <span>{displayTotal} cr</span>
                    </div>
                  </div>
                )}
                {!quote && (
                  <div className="flex justify-between font-semibold border-t pt-2 mt-2 text-sm">
                    <span>Total</span>
                    <span>{cartTotalCredits} cr</span>
                  </div>
                )}
                {quote && fulfillmentMethod === 'ship' && !quote.freeShippingApplied && quote.shippingCost > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    ℹ️ El costo de envío se cobra por orden. Si comprás varios productos del mismo vendedor, cada orden puede tener su propio envío. El total mostrado incluye 1 solo envío (caso más común).
                  </p>
                )}
              </div>

              {submitError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <p>{submitError}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {state === 'idle' && (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCheckout} disabled={!isFormValid}>
                  Confirmar compra ({displayTotal} cr)
                </Button>
              </>
            )}
            {(state === 'success' || state === 'partial' || state === 'error') && (
              <Button onClick={handleClose}>Cerrar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
